import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Alert } from "@material-ui/lab";
import { AutoDisabler, Validate, ValidationGroup } from "mui-validate";
import DeviceDataService from "../../../services/DeviceService";
import FloorService from "../../../services/FloorService";
import DeviceFloorService from "../../../services/DeviceFloorService";
import { Snackbar } from "@mui/material";
import AppBarComponent from "../../layout/AppBar";
import Scrollbar from "react-scrollbars-custom";
import clsx from "clsx";
import LocationService from "../../../services/LocationService";
import DeviceAssignment from "./DeviceAssignment";
import Toolbar from "@material-ui/core/Toolbar";
import AppBar from "@material-ui/core/AppBar";
import FloorDeviceTabs from "./FloorDeviceTabs";

const drawerWidth = 280;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  toolbar: {
    backgroundColor: "white",
    paddingRight: 24,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    height: "100vh",
    overflow: "auto",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  gridContainer: {
    display: "flex",
    "& > .MuiGrid-item": {
      display: "flex",
    },
  },
  card: {
    borderRadius: "4px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  cardContent: {
    padding: theme.spacing(2),
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: "250px", // Set a fixed minimum height for both cards
    "& .mui-validate__validate-root": {
      width: "95% !important",
    },
  },
  formContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  buttonWrapper: {
    marginTop: "auto",
    paddingTop: theme.spacing(2),
  },
}));

export default function FloorManagement() {
  const classes = useStyles();
  const [loc] = React.useState(() => {
    const locData = localStorage.getItem("loc");
    return locData ? JSON.parse(locData).id : "";
  });

  // State management
  const [floors, setFloors] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [newFloorName, setNewFloorName] = useState("");
  const [newFloorLevel, setNewFloorLevel] = useState("");
  const [snackbars, setSnackbars] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // First, add these new imports and state variables at the top of your component:
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Add this new function to fetch locations
  const retrieveLocations = async () => {
    try {
      const response = await LocationService.getAll();
      setLocations(response.data);
    } catch (error) {
      handleShowSnackbar("Грешка при зареждане на локациите", "error");
    }
  };

  // Load initial data
  useEffect(() => {
    retrieveLocations();
    retrieveDevices();
    retrieveFloors();
  }, []);

  // Fetch devices
  const retrieveDevices = async () => {
    try {
      const response = await DeviceDataService.adminAll();
      setDevices(response.data);
    } catch (error) {
      handleShowSnackbar("Грешка при зареждане на устройствата", "error");
    }
  };

  // Fetch floors
  const retrieveFloors = async () => {
    try {
      const response = await FloorService.getAll();
      setFloors(response.data);
    } catch (error) {
      handleShowSnackbar("Грешка при зареждане на етажите", "error");
    }
  };

  // Snackbar handling
  const handleShowSnackbar = (message, severity) => {
    const id = new Date().getTime();
    setSnackbars((prev) => [...prev, { id, message, severity }]);
  };

  const handleCloseSnackbar = (id) => {
    setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id));
  };

  // Floor creation
  const handleCreateFloor = async () => {
    if (!selectedLocation) {
      handleShowSnackbar("Моля, изберете локация", "warning");
      return;
    }

    try {
      setIsLoading(true);
      await FloorService.create({
        name: newFloorName,
        level: parseInt(newFloorLevel),
        location: selectedLocation.number, // Use number instead of id
      });
      handleShowSnackbar("Етажът е създаден успешно", "success");
      setNewFloorName("");
      setNewFloorLevel("");
      setSelectedLocation(null);
      retrieveFloors();
    } catch (error) {
      handleShowSnackbar("Грешка при създаване на етаж", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveDeviceFromFloor = async (floorId, deviceId) => {
    try {
      setIsLoading(true);
      await DeviceFloorService.bulkRemove(floorId, [deviceId]);
      handleShowSnackbar("Устройството е премахнато успешно", "success");
      retrieveDevices(); // Refresh the devices list
    } catch (error) {
      console.error("Remove device error:", error);
      handleShowSnackbar("Грешка при премахване на устройството", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBarComponent />
      <main className={classes.content}>
        {snackbars
          .slice()
          .reverse()
          .map((snackbar, index) => (
            <Snackbar
              key={snackbar.id}
              open={true}
              autoHideDuration={5000}
              onClose={() => handleCloseSnackbar(snackbar.id)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              style={{ bottom: `${10 + index * 55}px` }}
            >
              <Alert
                elevation={6}
                variant="filled"
                severity={snackbar.severity}
                onClose={() => handleCloseSnackbar(snackbar.id)}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          ))}
        <Scrollbar id="RSC-Example" style={{ width: "100%", height: "100%" }}>
          <div className={classes.appBarSpacer} />
          <Container maxWidth="lg" className={classes.container}>
            <Grid container spacing={1}>
              {/* Floor Creation Card */}
              <Grid item xs={12} lg={6}>
                <ValidationGroup>
                  <Card className={clsx(classes.card)}>
                    <AppBar position="static" elevation={0} color="primary">
                      <Toolbar className="px-8">
                        <Typography
                          variant="subtitle1"
                          color="inherit"
                          className="flex-1 px-12"
                        >
                          Създаване на нов етаж
                        </Typography>
                      </Toolbar>
                    </AppBar>
                    <CardContent className={classes.cardContent}>
                      <div className={classes.formContainer}>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Validate name="location" required>
                              <Autocomplete
                                value={selectedLocation}
                                onChange={(event, newValue) => {
                                  setSelectedLocation(newValue);
                                }}
                                fullWidth
                                options={locations}
                                getOptionLabel={(option) => option.name || ""}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Избери локация"
                                    variant="outlined"
                                    size="small"
                                    required
                                  />
                                )}
                              />
                            </Validate>
                          </Grid>
                          <Grid item xs={12}>
                            <Validate name="floorName" required>
                              <TextField
                                label="Име на етажа"
                                variant="outlined"
                                size="small"
                                fullWidth
                                required
                                value={newFloorName}
                                onChange={(e) =>
                                  setNewFloorName(e.target.value)
                                }
                              />
                            </Validate>
                          </Grid>
                          <Grid item xs={12}>
                            <Validate name="floorLevel" required>
                              <TextField
                                label="Ниво на етажа"
                                variant="outlined"
                                size="small"
                                fullWidth
                                required
                                type="number"
                                value={newFloorLevel}
                                onChange={(e) =>
                                  setNewFloorLevel(e.target.value)
                                }
                              />
                            </Validate>
                          </Grid>
                        </Grid>
                      </div>
                      <div className={classes.buttonWrapper}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-start",
                          }}
                        >
                          <AutoDisabler>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleCreateFloor}
                              disabled={isLoading}
                            >
                              Създай етаж
                            </Button>
                          </AutoDisabler>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </ValidationGroup>
              </Grid>

              {/* Device Assignment Card */}
              <Grid item xs={12} lg={6}>
                <DeviceAssignment
                  devices={devices}
                  floors={floors}
                  onAssignDevices={async (selectedFloor, selectedDevices) => {
                    try {
                      setIsLoading(true);

                      if (!selectedFloor.number) {
                        handleShowSnackbar("Невалиден етаж", "error");
                        return;
                      }

                      const validDevices = selectedDevices.filter(
                        (device) => device && device.id
                      );
                      if (validDevices.length === 0) {
                        handleShowSnackbar("Невалидни устройства", "error");
                        return;
                      }

                      const payload = {
                        floorId: selectedFloor.number,
                        deviceIds: validDevices.map((device) => device.id),
                      };

                      await DeviceFloorService.bulkAssign(payload);
                      handleShowSnackbar(
                        "Успешно зададохте стаите към етажа",
                        "success"
                      );
                      window.location.reload();
                    } catch (error) {
                      handleShowSnackbar(
                        "Грешка при задаването на етаж",
                        "error"
                      );
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  isLoading={isLoading}
                />
              </Grid>
            </Grid>
            <FloorDeviceTabs
              floors={floors}
              devices={devices}
              onRemoveDevice={handleRemoveDeviceFromFloor}
            />
          </Container>
        </Scrollbar>
      </main>
    </div>
  );
}
